import React, {memo, useEffect} from 'react'
import {union, orderBy, omit} from 'lodash'
import copy from 'clipboard-copy'
import {Button, Dropdown, Icon, Radio, Table} from 'semantic-ui-react'
import {withErrorHandling, withHelmet, withMeData, withPropsPresent, withUsersData, withNavigation} from '../../hoc'
import {atUserLimit, userAccount, userIsAdminForAccount, userIsBillingOwnerForAccount} from '../../util'
import {sendInvitation, setUserAsBillingOwner, toggleUserAsAdmin} from '../../backend'
import {useSafeState} from '../../hook'
import {Paged} from '../shared'

const userInvitation = (user, accountId) => {
  return user.invitations.find(i => i.account === accountId)
}

const userStatus = (user, accountId) => {
  const invitation = userInvitation(user, accountId)
  if (!invitation) {
    return 'Active'
  }
  return 'Pending'
}

const noUsersAreAdmin = (users, account) => {
  return !users.some((u) => {
    return userIsAdminForAccount(u, account)
  })
}

const atLeastOneOtherUserIsAdmin = (user, users, account) => {
  users = users.filter(u => u.id !== user.id)
  return users.some(u => {
    return userIsAdminForAccount(u, account)
  })
}

const noUsersAreBillingOwner = (users, account) => {
  return !users.some((u) => {
    return userIsBillingOwnerForAccount(u, account)
  })
}

const atLeastOneOtherUserIsBillingOwner = (user, users, account) => {
  users = users.filter(u => u.id !== user.id)
  return users.some(u => {
    return userIsBillingOwnerForAccount(u, account)
  })
}

const _Users = (props) => {
  const {
    me,
    users = [],
    location,
    navigate,
    basePath = '/c/account/users',
    sendInvitation,
    toggleUserAsAdmin,
    setUserAsBillingOwner,
    query,
    children
  } = props

  const account = userAccount(me, false)
  const mapUsers = (users) => {
    users = users.map(u => {
      return {
        ...u,
        first: u.first || '',
        last: u.last || '',
        status: userStatus(u, account.id),
        admin: userIsAdminForAccount(u, account),
        billingOwner: userIsBillingOwnerForAccount(u, account)
      }
    })
    return orderBy(users, ['first'], ['asc'])
  }

  const [state, setState] = useSafeState({
    adminUpdating: [],
    billingOwnerUpdating: [],
    sending: '',
    copied: [],
    resent: [],
    account,
    users: mapUsers(users),
    sort: {
      column: 'first',
      direction: 'ascending'
    }
  })

  useEffect(() => {
    setState({...state, users: mapUsers(users), account})
  }, [me, users])

  const onSortByColumn = (column) => {
    const sort = {...state.sort}
    sort.column = column
    sort.direction = sort.direction === 'ascending' ? 'descending' : 'ascending'
    navigate(location.pathname, {query: {...omit(query, ['activePage'])}})
    setState({...state, sort})
  }

  const onInvite = (e) => {
    e.preventDefault()
    navigate(`${basePath}/invite`)
  }

  const onRemove = (e, user) => {
    e.preventDefault()
    navigate(`${basePath}/${user.id}/remove`)
  }

  const onResendInvite = async (e, user) => {
    setState({...state, sending: user.id})
    await sendInvitation(user)
    setState({...state, sending: '', resent: union(state.resent, [user.id])})
    setTimeout(() => {
      setState({...state, resent: state.resent.filter(uid => uid !== user.id)})
    }, 3000)
  }

  const onCopyInvite = async (e, user) => {
    const {protocol, host} = location
    const urlBase = `${protocol}//${host}`
    const invitation = userInvitation(user, state.account.id)
    copy(`${urlBase}/bff/users/accept-invitation/${invitation.id}`)
    setState({...state, copied: union(state.copied, [user.id])})
    setTimeout(() => {
      setState({...state, copied: state.copied.filter(uid => uid !== user.id)})
    }, 3000)
  }

  const onToggleAdmin = async (user) => {
    setState({...state, adminUpdating: union(state.adminUpdating, [user.id])})
    const account = await toggleUserAsAdmin(user)
    setState({...state, adminUpdating: state.adminUpdating.filter(id => user.id === id), account})
  }

  const onToggleBillingOwner = async (user) => {
    setState({...state, billingOwnerUpdating: union(state.billingOwnerUpdating, [user.id])})
    const account = await setUserAsBillingOwner(user)
    setState({...state, users, billingOwnerUpdating: state.billingOwnerUpdating.filter(id => user.id === id), account})
  }

  const sort = (users) => {
    if (!!state.sort.column) {
      let column = state.sort.column
      switch (column) {
        case 'first':
          column = 'firstLower'
          break
        case 'last':
          column = 'lastLower'
          break
        case 'email':
          column = 'emailLower'
          break
      }
      const toLower = users.map(u => {
        return {
          ...u,
          firstLower: u.first.toLowerCase(),
          lastLower: u.last.toLowerCase(),
          emailLower: u.email.toLowerCase()
        }
      })
      users = orderBy(toLower, [column], [state.sort.direction === 'descending' ? 'desc' : 'asc'])
    }
    return users
  }

  const noAdminsSet = noUsersAreAdmin(users, state.account)
  const noBillingOwnerSet = noUsersAreBillingOwner(users, state.account)

  const noItemsContent = (
    <div>
      Click <a onClick={onInvite} style={{cursor: 'pointer'}}>here</a> to invite a user
    </div>
  )

  return (
    <>
      <Paged items={sort(state.users)} location={location} navigate={navigate} noItemsContent={noItemsContent}
             render={
               ({page}) => {
                 return (
                   <Table sortable celled collapsing style={{width: '100%'}}>
                     <Table.Header>
                       <Table.Row>
                         <Table.HeaderCell sorted={state.sort.column === 'first' ? state.sort.direction : null}
                                           onClick={() => onSortByColumn('first')}>First</Table.HeaderCell>
                         <Table.HeaderCell sorted={state.sort.column === 'last' ? state.sort.direction : null}
                                           onClick={() => onSortByColumn('last')}>Last</Table.HeaderCell>
                         <Table.HeaderCell sorted={state.sort.column === 'email' ? state.sort.direction : null}
                                           onClick={() => onSortByColumn('email')}>Email</Table.HeaderCell>
                         <Table.HeaderCell sorted={state.sort.column === 'status' ? state.sort.direction : null}
                                           onClick={() => onSortByColumn('status')}>Status</Table.HeaderCell>
                         <Table.HeaderCell sorted={state.sort.column === 'admin' ? state.sort.direction : null}
                                           onClick={() => onSortByColumn('admin')}>Admin</Table.HeaderCell>
                         <Table.HeaderCell sorted={state.sort.column === 'billingOwner' ? state.sort.direction : null}
                                           onClick={() => onSortByColumn('billingOwner')}>Billing
                           Owner</Table.HeaderCell>
                         <Table.HeaderCell></Table.HeaderCell>
                       </Table.Row>
                     </Table.Header>
                     <Table.Body>
                       {
                         page.map((u) => {
                             const canRemove = atLeastOneOtherUserIsAdmin(u, users, state.account) && atLeastOneOtherUserIsBillingOwner(u, users, state.account)
                             return (
                               <Table.Row key={u.id}>
                                 <Table.Cell data-test='user-first'>{u.first || 'n/a'}</Table.Cell>
                                 <Table.Cell data-test='user-last'>{u.last || 'n/a'}</Table.Cell>
                                 <Table.Cell data-test='user-email'>{u.email}</Table.Cell>
                                 <Table.Cell data-test='user-status'>{userStatus(u, state.account.id)}</Table.Cell>
                                 <Table.Cell>
                                   {
                                     state.adminUpdating.includes(u.id) && (
                                       <Button size='mini' loading={true}/>
                                     )
                                   }
                                   {
                                     !state.adminUpdating.includes(u.id) && (
                                       <Radio toggle checked={userIsAdminForAccount(u, state.account)}
                                              onChange={() => onToggleAdmin(u)}
                                              data-test='user-admin-radio'
                                              disabled={noAdminsSet || !atLeastOneOtherUserIsAdmin(u, users, state.account)}/>)
                                   }
                                 </Table.Cell>
                                 <Table.Cell>
                                   {
                                     state.billingOwnerUpdating.includes(u.id) && (
                                       <Button size='mini' loading={true}/>
                                     )
                                   }
                                   {
                                     !state.billingOwnerUpdating.includes(u.id) && (
                                       <Radio toggle checked={userIsBillingOwnerForAccount(u, state.account)}
                                              onChange={() => onToggleBillingOwner(u)}
                                              data-test='user-billing-owner-radio'
                                              disabled={noBillingOwnerSet || !atLeastOneOtherUserIsBillingOwner(u, users, state.account)}/>
                                     )
                                   }
                                 </Table.Cell>
                                 <Table.Cell textAlign='center' data-test='user-actions-table-cell'>
                                   {
                                     !!state.copied.includes(u.id) && (
                                       <>
                                         <Icon circular inverted primary name='check'/>
                                         <span>Copied</span>
                                       </>
                                     )
                                   }
                                   {
                                     !!state.resent.includes(u.id) && (
                                       <>
                                         <Icon circular inverted primary name='check'/>
                                         <span>Resent</span>
                                       </>
                                     )
                                   }
                                   {
                                     canRemove && !state.resent.length && !state.copied.length && (
                                       <Dropdown icon='setting' loading={state.sending === u.id}
                                                 data-test='user-actions-menu'>
                                         <Dropdown.Menu>
                                           {
                                             canRemove && (
                                               <Dropdown.Item as='a' onClick={(e) => onRemove(e, u)}
                                                              data-test='user-remove-action'>
                                                 <Icon name='remove'/> Remove
                                               </Dropdown.Item>
                                             )
                                           }
                                           {
                                             userInvitation(u, state.account.id) && (
                                               <>
                                                 <Dropdown.Item as='a' onClick={(e) => onResendInvite(e, u)}
                                                                data-test='user-resend-invite-action'>
                                                   <Icon
                                                     name='send'/> {state.resent.includes(u.id) ? 'Successfully Resent!' : 'Resend Invitation'}
                                                 </Dropdown.Item>
                                                 < Dropdown.Item as='a' onClick={(e) => onCopyInvite(e, u)}
                                                                 data-test='user-copy-invite-action'>
                                                   <Icon
                                                     name='copy'/> {state.copied.includes(u.id) ? 'Successfully Copied!' : 'Copy Invitation Link'}
                                                 </Dropdown.Item>
                                               </>
                                             )
                                           }
                                         </Dropdown.Menu>
                                       </Dropdown>
                                     )
                                   }
                                 </Table.Cell>
                               </Table.Row>
                             )
                           }
                         )
                       }
                     </Table.Body>
                     <Table.Footer>
                       <Table.Row>
                         <Table.HeaderCell colSpan='7'>
                         </Table.HeaderCell>
                       </Table.Row>
                     </Table.Footer>
                   </Table>
                 )
               }
             }>
      </Paged>
      <div style={{marginTop: '15px'}}>
        <Button size='tiny'
                basic
                icon='add'
                content='Invite User'
                style={{marginTop: '25px'}}
                disabled={atUserLimit(me)}
                data-testid='user-invite-button'
                onClick={onInvite}/>
      </div>
      {children}
    </>
  )
}

let Users = (props) => {
  const {handleError} = props
  props = {
    ...props,
    sendInvitation: handleError(sendInvitation),
    toggleUserAsAdmin: handleError(toggleUserAsAdmin),
    setUserAsBillingOwner: handleError(setUserAsBillingOwner)
  }
  return (
    <_Users {...props} default/>
  )
}


Users = withHelmet(Users, 'Users')
Users = withPropsPresent(Users, ['users', 'me'])
Users = withMeData(Users, 'Loading profile...')
Users = withUsersData(Users, 'Loading users...')
Users = withNavigation(Users)
Users = withErrorHandling(Users)
Users = memo(Users)

export {_Users, Users}

export default Users